import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, enforceTemporalBounds, enforceVisibilityInvariant, jsonScalarSchema, nonEmptyStringSchema, sha256Schema, tenantOwnedShape, visibilityShape, } from "./common.js";
import { aliasIdSchema, claimIdSchema, entityIdSchema, eventIdSchema, evidenceIdSchema, identifierIdSchema, knowledgeProcessingResultIdSchema, knowledgeProjectionIdSchema, observedStateIdSchema, relationIdSchema, resolvedFactIdSchema, tenantIdSchema, } from "./ids.js";
import { claimRefSchema, connectionRefSchema, documentBlockRefSchema, documentVersionRefSchema, entityRefSchema, evidenceRefSchema, relationRefSchema, sourceItemRefSchema, } from "./refs.js";
import { absenceCoverageStateSchema, claimStateSchema, entityTypeSchema, identifierStrengthByType, identifierStrengthSchema, identifierTypeSchema, knowledgeProcessingStateSchema, observedStateTypeSchema, relationTypeSchema, } from "./vocabulary.js";
export const entitySchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: entityIdSchema,
    entityType: entityTypeSchema,
    canonicalName: nonEmptyStringSchema.max(1000),
    status: z.enum(["active", "merged", "tombstoned"]),
    mergedIntoRef: entityRefSchema.nullable(),
    resolutionVersion: nonEmptyStringSchema.max(128),
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    if (value.mergedIntoRef) {
        enforceSameTenant(value.tenantId, [{ tenantId: value.mergedIntoRef.tenantId, path: ["mergedIntoRef"] }], context);
    }
    if ((value.status === "merged") !== (value.mergedIntoRef !== null)) {
        context.addIssue({
            code: "custom",
            path: ["mergedIntoRef"],
            message: "Merged entities require exactly one merge target.",
        });
    }
});
export const identifierSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: identifierIdSchema,
    entityRef: entityRefSchema,
    identifierType: identifierTypeSchema,
    strength: identifierStrengthSchema,
    namespace: nonEmptyStringSchema.max(512),
    normalizedValue: nonEmptyStringSchema.max(2048),
    displayValue: nonEmptyStringSchema.max(2048),
    sourceEvidenceRef: evidenceRefSchema,
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        { tenantId: value.entityRef.tenantId, path: ["entityRef"] },
        {
            tenantId: value.sourceEvidenceRef.tenantId,
            path: ["sourceEvidenceRef"],
        },
    ], context);
    if (value.strength !== identifierStrengthByType[value.identifierType]) {
        context.addIssue({
            code: "custom",
            path: ["strength"],
            message: "Identifier strength does not match the controlled vocabulary.",
        });
    }
});
export function identifierUniquenessKey(value) {
    return [
        value.tenantId,
        value.identifierType,
        value.namespace,
        value.normalizedValue,
    ].join("\u001f");
}
export const aliasSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: aliasIdSchema,
    entityRef: entityRefSchema,
    value: nonEmptyStringSchema.max(2048),
    normalizedValue: nonEmptyStringSchema.max(2048),
    evidenceRef: evidenceRefSchema,
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        { tenantId: value.entityRef.tenantId, path: ["entityRef"] },
        { tenantId: value.evidenceRef.tenantId, path: ["evidenceRef"] },
    ], context);
});
export const evidenceSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: evidenceIdSchema,
    sourceItemRef: sourceItemRefSchema,
    documentVersionRef: documentVersionRefSchema,
    documentBlockRef: documentBlockRefSchema,
    byteStartInBlock: z.number().int().nonnegative(),
    byteEndExclusiveInBlock: z.number().int().positive(),
    excerptSha256: sha256Schema,
    extractorVersion: nonEmptyStringSchema.max(128),
    extractorInputSha256: sha256Schema,
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        { tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] },
        {
            tenantId: value.documentVersionRef.tenantId,
            path: ["documentVersionRef"],
        },
        {
            tenantId: value.documentBlockRef.tenantId,
            path: ["documentBlockRef"],
        },
    ], context);
    if (value.byteEndExclusiveInBlock <= value.byteStartInBlock) {
        context.addIssue({
            code: "custom",
            path: ["byteEndExclusiveInBlock"],
            message: "Evidence byte range must be non-empty.",
        });
    }
});
const claimValueShape = {
    objectEntityRef: entityRefSchema.nullable(),
    scalarValue: jsonScalarSchema.nullable(),
};
export const claimSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: claimIdSchema,
    subjectEntityRef: entityRefSchema,
    predicate: relationTypeSchema,
    ...claimValueShape,
    state: claimStateSchema,
    evidenceRefs: z.array(evidenceRefSchema).min(1).max(1000),
    extractorVersion: nonEmptyStringSchema.max(128),
    extractorInputSha256: sha256Schema,
    validFrom: dateTimeSchema.nullable(),
    validTo: dateTimeSchema.nullable(),
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        {
            tenantId: value.subjectEntityRef.tenantId,
            path: ["subjectEntityRef"],
        },
        ...(value.objectEntityRef
            ? [
                {
                    tenantId: value.objectEntityRef.tenantId,
                    path: ["objectEntityRef"],
                },
            ]
            : []),
        ...value.evidenceRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["evidenceRefs", index],
        })),
    ], context);
    if ((value.objectEntityRef === null) === (value.scalarValue === null)) {
        context.addIssue({
            code: "custom",
            path: ["scalarValue"],
            message: "A claim requires exactly one object entity or scalar value.",
        });
    }
    enforceTemporalBounds(value, context);
});
export const eventRefSchema = z
    .object({ tenantId: tenantIdSchema, eventId: eventIdSchema })
    .strict();
export const eventSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: eventIdSchema,
    eventType: relationTypeSchema,
    subjectEntityRefs: z.array(entityRefSchema).min(1).max(100),
    occurredAt: dateTimeSchema.nullable(),
    state: claimStateSchema,
    evidenceRefs: z.array(evidenceRefSchema).min(1).max(1000),
    extractorVersion: nonEmptyStringSchema.max(128),
    extractorInputSha256: sha256Schema,
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        ...value.subjectEntityRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["subjectEntityRefs", index],
        })),
        ...value.evidenceRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["evidenceRefs", index],
        })),
    ], context);
});
export const relationSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: relationIdSchema,
    fromEntityRef: entityRefSchema,
    toEntityRef: entityRefSchema,
    relationType: relationTypeSchema,
    state: claimStateSchema,
    evidenceRefs: z.array(evidenceRefSchema).min(1).max(1000),
    extractorVersion: nonEmptyStringSchema.max(128),
    extractorInputSha256: sha256Schema,
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        { tenantId: value.fromEntityRef.tenantId, path: ["fromEntityRef"] },
        { tenantId: value.toEntityRef.tenantId, path: ["toEntityRef"] },
        ...value.evidenceRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["evidenceRefs", index],
        })),
    ], context);
});
export const knowledgeProcessingResultSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: knowledgeProcessingResultIdSchema,
    documentVersionRef: documentVersionRefSchema,
    status: knowledgeProcessingStateSchema,
    claimRefs: z.array(claimRefSchema).max(10000),
    eventRefs: z.array(eventRefSchema).max(10000),
    relationRefs: z.array(relationRefSchema).max(10000),
    extractorVersion: nonEmptyStringSchema.max(128),
    extractorInputSha256: sha256Schema,
    errorCode: nonEmptyStringSchema.max(256).nullable(),
    processedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    const outputCount = value.claimRefs.length +
        value.eventRefs.length +
        value.relationRefs.length;
    enforceSameTenant(value.tenantId, [
        {
            tenantId: value.documentVersionRef.tenantId,
            path: ["documentVersionRef"],
        },
        ...value.claimRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["claimRefs", index],
        })),
        ...value.eventRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["eventRefs", index],
        })),
        ...value.relationRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["relationRefs", index],
        })),
    ], context);
    if (value.status === "processed_with_links" && outputCount === 0) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "processed_with_links requires at least one output reference.",
        });
    }
    if (value.status === "processed_no_supported_claim" && outputCount !== 0) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "processed_no_supported_claim must not contain semantic outputs.",
        });
    }
});
export const resolvedFactSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: resolvedFactIdSchema,
    subjectEntityRef: entityRefSchema,
    predicate: relationTypeSchema,
    value: jsonScalarSchema,
    selectedClaimRefs: z.array(claimRefSchema).min(1).max(1000),
    conflictingClaimRefs: z.array(claimRefSchema).max(1000),
    ruleVersion: nonEmptyStringSchema.max(128),
    validFrom: dateTimeSchema.nullable(),
    validTo: dateTimeSchema.nullable(),
    computedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        {
            tenantId: value.subjectEntityRef.tenantId,
            path: ["subjectEntityRef"],
        },
        ...value.selectedClaimRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["selectedClaimRefs", index],
        })),
        ...value.conflictingClaimRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["conflictingClaimRefs", index],
        })),
    ], context);
    enforceTemporalBounds(value, context);
});
export const observedCoverageSourceSchema = z
    .object({
    connectionRef: connectionRefSchema,
    containerId: nonEmptyStringSchema.max(2048),
    watermark: dateTimeSchema.nullable(),
    required: z.boolean(),
    reconciliationState: z.enum([
        "reconciled",
        "behind",
        "failed",
        "inaccessible",
        "unmapped",
    ]),
})
    .strict();
export const resolvedFactRefSchema = z
    .object({ tenantId: tenantIdSchema, resolvedFactId: resolvedFactIdSchema })
    .strict();
const observedStateObjectSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: observedStateIdSchema,
    stateType: observedStateTypeSchema,
    stateValue: jsonScalarSchema,
    asOf: dateTimeSchema,
    computationVersion: nonEmptyStringSchema.max(128),
    coveredSources: z.array(observedCoverageSourceSchema).min(1).max(10000),
    requiredSourceCount: z.number().int().nonnegative(),
    reconciledSourceCount: z.number().int().nonnegative(),
    completenessRatio: z.number().min(0).max(1),
    coverageState: absenceCoverageStateSchema,
    evidenceRefs: z.array(evidenceRefSchema).min(1).max(1000),
    computedAt: dateTimeSchema,
})
    .strict();
function coverageIsComplete(value) {
    const required = value.coveredSources.filter((source) => source.required);
    return (required.length === value.requiredSourceCount &&
        value.reconciledSourceCount === value.requiredSourceCount &&
        required.every((source) => source.reconciliationState === "reconciled" &&
            source.watermark !== null));
}
export const observedStateSchema = observedStateObjectSchema.superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        ...value.coveredSources.map((source, index) => ({
            tenantId: source.connectionRef.tenantId,
            path: ["coveredSources", index, "connectionRef"],
        })),
        ...value.evidenceRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["evidenceRefs", index],
        })),
    ], context);
    const expectedRatio = value.requiredSourceCount === 0
        ? 1
        : value.reconciledSourceCount / value.requiredSourceCount;
    if (Math.abs(value.completenessRatio - expectedRatio) > 1e-9) {
        context.addIssue({
            code: "custom",
            path: ["completenessRatio"],
            message: "Completeness ratio does not match reconciled and required source counts.",
        });
    }
    if (!coverageIsComplete(value) &&
        (value.coverageState !== "unknown" || value.stateValue !== "unknown")) {
        context.addIssue({
            code: "custom",
            path: ["coverageState"],
            message: "Incomplete required coverage must produce an unknown observed state.",
        });
    }
});
export function applyObservedStateCoverageRule(input) {
    const value = observedStateObjectSchema.parse(input);
    const normalized = coverageIsComplete(value)
        ? value
        : {
            ...value,
            coverageState: "unknown",
            stateValue: "unknown",
        };
    return observedStateSchema.parse(normalized);
}
export const knowledgeProjectionSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: knowledgeProjectionIdSchema,
    projectionType: z.enum([
        "company",
        "customer",
        "project",
        "open_commitments",
        "recent_decisions",
        "upcoming_dates",
    ]),
    subjectEntityRef: entityRefSchema.nullable(),
    factRefs: z.array(resolvedFactRefSchema).max(10000),
    evidenceRefs: z.array(evidenceRefSchema).min(1).max(10000),
    fields: z.record(z.string().min(1).max(128), jsonScalarSchema),
    computationVersion: nonEmptyStringSchema.max(128),
    asOf: dateTimeSchema,
    computedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        ...(value.subjectEntityRef
            ? [
                {
                    tenantId: value.subjectEntityRef.tenantId,
                    path: ["subjectEntityRef"],
                },
            ]
            : []),
        ...value.factRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["factRefs", index],
        })),
        ...value.evidenceRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["evidenceRefs", index],
        })),
    ], context);
});
//# sourceMappingURL=knowledge.js.map